import React from "react";
import ReactDOM from "react-dom";
import "./App.css";
import { ZoomMtg } from "@zoom/meetingsdk";

ZoomMtg.preLoadWasm();
ZoomMtg.prepareWebSDK();

const socketUrl = process.env.REACT_APP_SOCKET_URL || "wss://api.nrichlearning.com/ws";

const DEFAULT_PRODUCT_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' rx='8' fill='%23e5e7eb'/%3E%3Cpath d='M20 40l8-10 6 7 5-6 9 9v2a2 2 0 01-2 2H22a2 2 0 01-2-2z' fill='%23a1a1aa'/%3E%3Ccircle cx='24' cy='24' r='4' fill='%23a1a1aa'/%3E%3C/svg%3E";

const encodeRouteId = (id) =>
  btoa(String(id)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const stripHtmlTags = (value) =>
  typeof value === "string" ? value.replace(/<[^>]*>/g, "").trim() : value;

function getRecommendedProductType(product) {
  return (product?.recommendedProductType || product?.productLabel || "").toUpperCase();
}

function buildRecommendationUrl(product) {
  const domain = product?.domainUrl;
  const type = getRecommendedProductType(product);
  let path;

  switch (type) {
    case "COURSE": {
      const id = encodeRouteId(product?.courseId ?? product?.recommendedProductId);
      const slug = product?.courseSlug ? `/${product?.courseSlug}` : "";
      path = `/course/${id}${slug}`;
      break;
    }
    case "BOOK":
      path = `/book/${encodeRouteId(product?.recommendedProductId)}`;
      break;
    case "STUDY_MATERIAL":
      path = `/study-material/${encodeRouteId(product?.recommendedProductId)}`;
      break;
    case "WEBINAR":
      path = `/webinar/${encodeRouteId(product?.recommendedProductId)}`;
      break;
    case "MODULE":
    default:
      path = `/module/${encodeRouteId(product?.recommendedProductId)}`;
  }

  return `${domain}${path}?recommendation=true&recommendedProductId=${product?.id ?? ""}&contextId=${product?.contextId ?? ""}`;
}

function buildPayNowUrl(product) {
  const domain = product?.domainUrl;
  const type = getRecommendedProductType(product);

  if (type === "WEBINAR") {
    return `${domain}/registration/webinar/${encodeRouteId(product?.recommendedProductId)}`;
  }

  const params = {
    instituteId: product?.instituteId,
    productId: product?.courseId ?? product?.recommendedProductId,
    productType: (product?.recommendedProductType || "course").toLowerCase(),
    isPublic: true,
    instituteName: product?.instituteName,
    instituteImage: product?.instituteImage,
  };

  Object.keys(params).forEach((key) => {
    if (params[key] === null || params[key] === undefined || params[key] === "") {
      delete params[key];
    }
  });

  const encoded = btoa(encodeURIComponent(JSON.stringify(params)));
  return `${domain}/student/payment/link?data=${encoded}`;
}

class App extends React.Component {
  pauseAndPlayRecSubscription;
  iconSubscription;
  classId;
  instituteId;
  accessToken;
  role;
  isJoinClass;
  meetingDetails = null;
  feedbackEnabled = false;
  feedbackCollectionEnabled = false;
  ws = null;
  wsReconnectAttempts = 0;
  wsMaxReconnects = 5;
  wsReconnectTimer = null;
  feedbackDismissed = false;
  sessionEnded = false;
  isWebinar = false;
  webinarEngagementLoaded = false;
  webinarFeedbackAllowed = false;

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      checkingDesktop: false,
      showWebFallback: false,
      showAppLaunchPrompt: false,
      showFeedbackForm: false,
      feedbackText: "",
      selectedRating: null,
      feedbackSubmitted: false,
      recommendations: [],
      sessionTimedOut: false,
      liveQuiz: null,
    };
  }

  createAndSubscribeSocket(userId) {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.ws = new WebSocket(socketUrl);
    this._wsMsgCount = 0;

    this.ws.onopen = () => {
      this.wsReconnectAttempts = 0;
      const connectFrame =
        "CONNECT\n" +
        `Authorization:Bearer ${this.accessToken}\n` +
        "accept-version:1.1,1.2\n" +
        "heart-beat:0,0\n" +
        "\n\0";
      this.ws.send(connectFrame);
    };

    this.ws.onmessage = (event) => {
      if (typeof event.data !== "string") return;

      if (event.data.startsWith("CONNECTED")) {
        const subscribeFrame =
          "SUBSCRIBE\n" +
          "id:sub-0\n" +
          "destination:/user/queue/feedback\n" +
          "\n\0";
        this.ws.send(subscribeFrame);

        const subscribeRecommendationFrame =
          "SUBSCRIBE\n" +
          "id:sub-1\n" +
          `destination:/user/queue/webinar/${this.classId}/recommendation-product\n` +
          "\n\0";
        this.ws.send(subscribeRecommendationFrame);

        const subscribeLiveQuizFrame =
          "SUBSCRIBE\n" +
          "id:sub-2\n" +
          "destination:/user/queue/live-quiz-started\n" +
          "\n\0";
        this.ws.send(subscribeLiveQuizFrame);
        return;
      }

      if (!event.data.startsWith("MESSAGE")) return;

      const [rawHeaders, rawBody] = event.data.split("\n\n");
      const headers = {};
      rawHeaders.split("\n").forEach((line) => {
        const idx = line.indexOf(":");
        if (idx > -1) headers[line.slice(0, idx)] = line.slice(idx + 1);
      });
      const body = (rawBody || "").replace(/\0$/, "");

      if (headers.destination?.includes("/recommendation-product")) {
        try {
          this.showRecommendationCard(JSON.parse(body));
        } catch (e) {
          console.error("[WS] Failed to parse recommendation payload:", e);
        }
        return;
      }

      if (headers.destination?.includes("/queue/live-quiz-started")) {
        try {
          const payload = JSON.parse(body);
          if (payload?.joinUrl) {
            this.setState({ liveQuiz: payload });
          }
        } catch (e) {
          console.error("[WS] Failed to parse live quiz payload:", e);
        }
        return;
      }

      if (headers.destination?.includes("/queue/feedback")) {
        if (this.role?.toLowerCase() === "student") {
          this.feedbackEnabled = true;
          this.feedbackDismissed = false;
          if (this.isJoinClass && this.isFeedbackAllowed()) {
            this.setState({ showFeedbackForm: true, feedbackSubmitted: false });
          }
        }
      }
    };

    this.ws.onerror = (error) => {
      console.error("[WS] Error:", error);
    };

    this.ws.onclose = () => {
      this.attemptReconnect(userId);
    };
  }

  attemptReconnect(userId) {
    if (this.wsReconnectAttempts >= this.wsMaxReconnects) {
      return;
    }
    const delay = Math.min(1000 * 2 ** this.wsReconnectAttempts, 30000);
    this.wsReconnectAttempts += 1;
    this.wsReconnectTimer = setTimeout(() => {
      this.createAndSubscribeSocket(userId);
    }, delay);
  }

  showRecommendationCard(payload) {
    if (!payload) return;
    const entries = Array.isArray(payload.recommendations)
      ? payload.recommendations
      : payload.product
        ? [payload]
        : [];

    const recommendations = entries
      .filter((entry) => entry?.product)
      .map((entry) => ({
        ...entry.product,
        domainUrl: payload.domainUrl,
        instituteId: entry.product?.instituteId ?? payload.instituteId ?? this.instituteId,
        instituteName: entry.product?.instituteName ?? payload.instituteName,
        instituteImage: entry.product?.instituteImage ?? payload.instituteImage,
      }))
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    this.setState({ recommendations });
  }

  dismissRecommendation(productId) {
    this.setState((prevState) => ({
      recommendations: prevState.recommendations.filter((p) => p.id !== productId),
    }));
  }

  handleRecommendationClick(product, mode) {
    const url = mode === "pay" ? buildPayNowUrl(product) : buildRecommendationUrl(product);
    window.open(url, "_blank", "noopener,noreferrer");

    fetch(
      process.env.REACT_APP_API_URL + "users/v2/api/trackRecommendedProductEvent",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + this.accessToken,
        },
        body: JSON.stringify({
          recommendedProductId: product.id,
          classId: this.classId,
          userId: this.meetingDetails?.userId,
          eventType: "CLICK",
        }),
      }
    ).catch(() => { });
  }

  fetchWebinarEngagementSettings() {
    return fetch(
      process.env.REACT_APP_API_URL + "users/v2/api/webinarEngagementSettings?classId=" + this.classId,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + this.accessToken,
        },
      }
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((response) => {
        const body = response?.body ?? response;
        if (!body || typeof body.feedbackAllowed !== "boolean") return;
        this.webinarEngagementLoaded = true;
        this.webinarFeedbackAllowed = body.feedbackAllowed === true;
        if (!this.webinarFeedbackAllowed && this.state.showFeedbackForm) {
          this.setState({ showFeedbackForm: false });
        }
      })
      .catch((error) => {
        console.error("[WebinarEngagementSettings] Error:", error);
      });
  }

  isFeedbackAllowed() {
    if (!this.isWebinar) return true;
    return this.webinarEngagementLoaded && this.webinarFeedbackAllowed === true;
  }

  async onParticipantExit() {
    try {
      await fetch(
        process.env.REACT_APP_API_URL + "users/v1/api/participants/exit/" + this.classId,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + this.accessToken,
          },
        }
      );
      this.clearSessionId();
    } catch (error) {
      console.error("[Exit] Error:", error);
    }
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    window.addEventListener("unload", this.handleUnload);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    try {
      const query = window.location.search.split("?")[1] || "";
      let params = query.split("&");
      let meetingType = "";
      if (params[0] && params[0].toLowerCase().indexOf("type=") === 0) {
        meetingType = params[0].slice(5).toLowerCase();
        params = params.slice(1);
      }

      this.classId = params[0];
      this.instituteId = params[1];
      this.accessToken = params[2];
      const rawRole = params[3];
      this.role = rawRole
        ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase()
        : rawRole;
      const isFromLMS = params[4];

      this.isWebinar = meetingType === "webinar";
      const isFreshEntry = !!isFromLMS || !!meetingType;
      let isFromSession = false;
      try {
        isFromSession = sessionStorage.getItem("isFromLMS") === "true";
      } catch (e) { }

      if (isFreshEntry) {
        try {
          sessionStorage.setItem("isFromLMS", "true");
        } catch (e) { }
        const cleanParams = (meetingType ? [`type=${meetingType}`] : []).concat(params.slice(0, 4));
        const newUrl = `${window.location.pathname}?${cleanParams.filter(Boolean).join("&")}`;
        window.history.replaceState({}, "", newUrl);
      }

      if (this.classId && this.accessToken && (isFreshEntry || isFromSession)) {
        this.onParticipantJoining();
        if (this.isWebinar) {
          const settings = this.fetchWebinarEngagementSettings();
          const timeout = new Promise((resolve) => setTimeout(resolve, 6000));
          Promise.race([settings, timeout]).then(() => this.initializeMeeting());
        } else {
          this.initializeMeeting();
        }
      } else {
        window.location.href = "/invalid-link";
      }
    } catch (err) {
      window.location.href = "/invalid-link";
    }
  }

  handleVisibilityChange = () => {
    if (this.sessionEnded) return;
    if (document.visibilityState === "visible") {
      this.onParticipantJoining();
    } else {
      this.onParticipantExit();
      this.clearSessionId();
    }
  };

  handleUnload = () => {
    this.onParticipantExit();
    this.clearSessionId();
  };

  handleBeforeUnload = () => {
    this.onParticipantExit();
    this.clearSessionId();
  };

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener("unload", this.handleUnload);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.onParticipantExit();
    if (this.wsReconnectTimer) clearTimeout(this.wsReconnectTimer);
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
    }
  }

  clearSessionId() {
    localStorage.removeItem("session_id");
  }

  initializeMeeting() {
    fetch(
      process.env.REACT_APP_API_URL + "users/v1/api/getMeetingDetails?idClassSchedule=" + this.classId,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + this.accessToken,
        },
      }
    )
      .then((res) => res.json())
      .then((response) => {
        if (response.status === 201) {
          this.meetingDetails = {
            meetingNumber: response.body.id,
            userName: response.body.name,
            userEmail: response.body.hostEmail,
            passWord: response.body.password,
            role: response.body.role,
            userId: response.body.userId,
            signature: response.body.signature,
            responseBody: response.body,
          };

          if (response.body.endTime <= 0) {
            this.closeTab("This class has already ended. You cannot join.");
            return;
          }

          this.fetchClassSettings(response.body.endTime);

          if (this.role === "Student") {
            this.setState({ checkingDesktop: false });
            this.handleJoinWeb();
          } else {
            this.setState({ isLoading: false, showAppLaunchPrompt: true });
          }
        } else if (response.status === 401) {
          this.closeTab("You are already logout.");
        } else {
          this.closeTab("Something went wrong");
        }
      })
      .catch((error) => {
        console.error("Error while initializing meeting:", error);
      });
  }

  getDeviceType() {
    const ua = navigator.userAgent || "";
    if (/Android/i.test(ua)) return "android";
    if (/iPhone|iPad/i.test(ua)) return "ios";
    if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return "ios";
    return "desktop";
  }

  buildZoomAppUrl() {
    const { meetingNumber, passWord } = this.meetingDetails;
    const encodedPassword = encodeURIComponent(passWord || "");
    const device = this.getDeviceType();
    if (device === "android") {
      return `intent://zoom.us/join?confno=${meetingNumber}&pwd=${encodedPassword}#Intent;scheme=zoomus;package=us.zoom.videomeetings;end`;
    }
    const scheme = device === "ios" ? "zoomus" : "zoommtg";
    return `${scheme}://zoom.us/join?confno=${meetingNumber}&pwd=${encodedPassword}`;
  }

  fetchZoomStartUrl() {
    return fetch(
      process.env.REACT_APP_API_URL + "users/v1/api/zoom/class/" + this.classId + "/start-url",
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + this.accessToken,
        },
      }
    )
      .then((res) => (res.ok ? res.text() : null))
      .then((text) => {
        if (!text || !text.trim()) return null;
        const startUrl = JSON.parse(text)?.body?.startUrl;
        return typeof startUrl === "string" && startUrl.trim() ? startUrl.trim() : null;
      })
      .catch((error) => {
        console.error("[StartUrl] Error:", error);
        return null;
      });
  }

  attemptAppJoin = async () => {
    this.setState({ checkingDesktop: true, showWebFallback: false, showAppLaunchPrompt: false });

    const startUrl = await this.fetchZoomStartUrl();
    if (!startUrl) {
      // No start URL (empty body / missing field / API error) - fall back to normal join
      this.attemptDeepLinkJoin();
      return;
    }

    this.joinViaStartUrl(startUrl);
  };

  joinViaStartUrl = (startUrl) => {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener("unload", this.handleUnload);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.sessionEnded = true;
    this.onParticipantJoining();
    window.location.href = startUrl;
  };

  attemptDeepLinkJoin = () => {
    const zoomURL = this.buildZoomAppUrl();
    let checkTimer = null;
    let fallbackRevealTimer = null;
    let visibilityTimer = null;
    let didClose = false;

    const handleVisibilityChange = () => {
      if (document.hidden && !didClose) {
        visibilityTimer = setTimeout(() => {
          if (document.hidden && !didClose) {
            didClose = true;
            cleanup();
            this.onParticipantJoining();
            window.close();
          }
        }, 2000);
      } else if (!document.hidden && visibilityTimer) {
        clearTimeout(visibilityTimer);
        visibilityTimer = null;
      }
    };

    const cleanup = () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (checkTimer) clearTimeout(checkTimer);
      if (fallbackRevealTimer) clearTimeout(fallbackRevealTimer);
      if (visibilityTimer) clearTimeout(visibilityTimer);
      this.webFallbackHandler = null;
    };

    this.webFallbackHandler = () => {
      if (didClose) return;
      didClose = true;
      cleanup();
      this.setState({ checkingDesktop: false, showWebFallback: false });
      this.handleJoinWeb();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.location.href = zoomURL;

    fallbackRevealTimer = setTimeout(() => {
      if (!didClose && !document.hidden) {
        this.setState({ showWebFallback: true });
      }
    }, 3000);

    checkTimer = setTimeout(() => {
      if (didClose) return;
      const isHidden = document.hidden;
      const hasFocus = document.hasFocus();
      if (isHidden || !hasFocus) {
        didClose = true;
        cleanup();
        this.onParticipantJoining();
        window.close();
      } else {
        didClose = true;
        cleanup();
        this.setState({ checkingDesktop: false, showWebFallback: false });
        this.handleJoinWeb();
      }
    }, 5000);
  };

  handleJoinWeb = () => {
    this.setState({ showPopup: false, isLoading: true });
    const root = document.getElementById("zmmtg-root");
    if (root) root.style.display = "block";
    const { meetingNumber, userName, userEmail, passWord, userId, signature } = this.meetingDetails;
    this.joinViaWebSDK(meetingNumber, userName, userEmail, passWord, userId, signature);
  };

  joinViaWebSDK(meetingNumber, userName, userEmail, passWord, userId, signature) {
    ZoomMtg.init({
      leaveUrl: process.env.REACT_APP_LEAVE_URL,
      disableInvite: true,
      disableZoomLogo: true,
      defaultView: "gallery",
      patchJsMedia: true,
      leaveOnPageUnload: true,
      success: () => {
        ZoomMtg.join({
          signature: signature,
          meetingNumber: meetingNumber,
          userName: userName == null ? "Test" : userName,
          sdkKey: process.env.REACT_APP_SDK_KEY,
          customerKey: userId + "/" + this.classId,
          userEmail: userEmail,
          passWord: passWord,
          success: () => {
            this.isJoinClass = true;
            if (this.role?.toLowerCase() !== "admin") {
              this.createAndSubscribeSocket(this.parseJwt(this.accessToken));
            }
            this.setState({ isLoading: false });
            this.tryShowFeedbackForm();
            this.onParticipantJoining();
            const appSignalElement = document.getElementById("app-signal");
            if (appSignalElement) appSignalElement.style.display = "none";
            this.removePauseAndPlayRecordingBtn();
            this.removeRecordingIcons();
          },
          error: (error) => {
            console.log("Zoom join error:", error, {
              meetingNumber,
              hasSignature: !!signature,
              sdkKey: process.env.REACT_APP_SDK_KEY,
              role: this.role,
              classId: this.classId,
            });
            this.setState({ isLoading: false });
            const reason = error?.reason || error?.errorMessage || error?.message || "";
            const code = error?.errorCode != null ? ` (code ${error.errorCode})` : "";
            alert("Failed to join meeting. Please try again." + (reason || code ? `\n${reason}${code}` : ""));
          },
        });
      },
      error: (error) => {
        this.setState({ isLoading: false });
        alert("Failed to initialize Zoom. Please try again.");
      },
    });
  }

  closeTab(msg) {
    alert(msg);
    setTimeout(() => {
      window.close();
    }, 2000);
  }

  async onParticipantJoining() {
    if (this.sessionEnded) return;
    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + "users/v1/api/participants/entry/" + this.classId,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + this.accessToken,
          },
        }
      );
      const responseData = await response.json();
      if (responseData.status === 400) {
        this.closeTab(responseData.message);
      }
    } catch (err) {
      console.error("Error during participant join:", err);
    }
  }

  removePauseAndPlayRecordingBtn() {
    this.pauseAndPlayRecSubscription = setInterval(() => {
      var htmlCollection = document.getElementsByClassName("footer-button-pause-stop-recording");
      if (htmlCollection && htmlCollection.length !== 0) {
        for (let element of htmlCollection) element.remove();
        clearInterval(this.pauseAndPlayRecSubscription);
      }
    }, 500);
  }

  removeRecordingIcons() {
    this.iconSubscription = setInterval(() => {
      var htmlCollection = document.getElementsByClassName("meeting-info-container__wrapper");
      if (htmlCollection && htmlCollection.length !== 0) {
        for (let element of htmlCollection) element.remove();
        clearInterval(this.iconSubscription);
      }
    }, 500);
  }

  parseJwt(token) {
    try {
      const base64Payload = token.split(".")[1];
      const decoded = atob(base64Payload.replace(/-/g, "+").replace(/_/g, "/"));
      const payload = decodeURIComponent(
        decoded
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(payload);
    } catch (e) {
      console.error("Invalid JWT token parsing:", e);
      return {};
    }
  }

  tryShowFeedbackForm() {
    if (
      this.isJoinClass &&
      this.feedbackEnabled &&
      this.role === "Student" &&
      !this.feedbackDismissed &&
      this.isFeedbackAllowed()
    ) {
      this.setState({ showFeedbackForm: true, feedbackSubmitted: false });
    }
  }

  fetchClassSettings(endTime) {
    fetch(
      process.env.REACT_APP_API_URL + "users/v2/api/settings/class/" + this.instituteId,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + this.accessToken,
        },
      }
    )
      .then((res) => res.json())
      .then((response) => {
        const enabled = response?.body?.feedbackCollectionEnabled;
        if (enabled === true || enabled === "true" || enabled === 1) {
          this.feedbackCollectionEnabled = true;
        }
      })
      .catch((error) => {
        console.error("Error fetching class settings:", error);
      })
      .finally(() => {
        const fiveMinMs = 5 * 60 * 1000;
        const feedbackDelay = Math.max(0, endTime * 1000 - fiveMinMs);

        setTimeout(() => {
          if (
            (this.role === "Student" || this.role === "STUDENT") &&
            this.isJoinClass &&
            !this.feedbackDismissed &&
            this.isFeedbackAllowed()
          ) {
            this.setState({ showFeedbackForm: true, feedbackSubmitted: false });
          }
        }, feedbackDelay);

        this.sessionOut(endTime);
      });
  }

  sessionOut(endTime) {
    setTimeout(() => {
      this.sessionEnded = true;
      if (this.wsReconnectTimer) clearTimeout(this.wsReconnectTimer);
      if (this.ws) {
        this.ws.onclose = null;
        this.ws.close();
      }
      // Non-blocking notice (no OK click needed) — auto-close the tab in 2s.
      this.setState({ sessionTimedOut: true });
      setTimeout(() => {
        window.close();
      }, 2000);
    }, endTime * 1000);
  }

  async submitFeedback() {
    const { feedbackText, selectedRating } = this.state;
    if (!selectedRating || !feedbackText.trim()) return;
    try {
      await fetch(
        process.env.REACT_APP_API_URL + "users/v2/api/feedback/submission",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + this.accessToken,
          },
          body: JSON.stringify({
            id: null,
            classId: this.classId,
            review: feedbackText.trim(),
            reaction: selectedRating,
            studentId: null,
          }),
        }
      );
    } catch (error) {
      console.error("Error submitting feedback:", error);
    }
    this.feedbackDismissed = true;
    this.setState({
      showFeedbackForm: false,
      feedbackSubmitted: true,
      feedbackText: "",
      selectedRating: null,
    });
  }

  render() {
    const {
      isLoading,
      checkingDesktop,
      showWebFallback,
      showAppLaunchPrompt,
      showFeedbackForm,
      feedbackText,
      selectedRating,
      recommendations,
      sessionTimedOut,
      liveQuiz,
    } = this.state;

    const ratings = [
      { emoji: "😍", label: "Amazing", value: "Amazing" },
      { emoji: "😊", label: "Good", value: "Good" },
      { emoji: "😐", label: "Average", value: "Average" },
      { emoji: "😕", label: "Below Average", value: "Below Average" },
    ];

    const feedbackPortal = showFeedbackForm
      ? ReactDOM.createPortal(
        <div style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: 2147483647,
          backgroundColor: "#ffffff",
          border: "1px solid #e8e8e8",
          borderRadius: "10px",
          padding: "24px",
          width: "320px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
          fontFamily: "'Poppins', sans-serif",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
            <div>
              <h2 style={{ fontSize: "1rem", fontWeight: 700, color: "#1a1a1a", margin: "0 0 4px" }}>
                How was your experience?
              </h2>
              <p style={{ fontSize: "0.82rem", color: "#999", margin: 0 }}>
                We'd love to hear your feedback
              </p>
            </div>
            <button
              onClick={() => {
                this.feedbackDismissed = true;
                this.setState({ showFeedbackForm: false, feedbackText: "", selectedRating: null });
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "20px",
                color: "#999",
                lineHeight: 1,
                padding: "0",
                marginLeft: "8px",
              }}
            >
              &times;
            </button>
          </div>

          <div style={{ display: "flex", justifyContent: "space-around", marginBottom: "24px" }}>
            {ratings.map((r) => (
              <button
                key={r.value}
                onClick={() => this.setState({ selectedRating: r.value })}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "6px",
                  background: selectedRating === r.value ? "#fff8ec" : "none",
                  border: `2px solid ${selectedRating === r.value ? "#f5a623" : "transparent"}`,
                  borderRadius: "14px",
                  padding: "10px 12px",
                  cursor: "pointer",
                  transition: "all 0.22s",
                }}
              >
                <span style={{
                  fontSize: "2rem",
                  lineHeight: 1,
                  display: "block",
                  transform: selectedRating === r.value ? "scale(1.25)" : "scale(1)",
                  transition: "transform 0.22s",
                }}>
                  {r.emoji}
                </span>
                <span style={{
                  fontSize: "9px",
                  color: selectedRating === r.value ? "#c47d00" : "#999",
                  fontWeight: selectedRating === r.value ? 700 : 500,
                  whiteSpace: "nowrap",
                  fontFamily: "'Poppins', sans-serif",
                }}>
                  {r.label}
                </span>
              </button>
            ))}
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              fontSize: "0.82rem",
              fontWeight: 700,
              color: "#1a1a1a",
              marginBottom: "8px",
              fontFamily: "'Poppins', sans-serif",
            }}>
              Your Review
            </label>
            <textarea
              rows={4}
              value={feedbackText}
              onChange={(e) => this.setState({ feedbackText: e.target.value })}
              placeholder="Tell us more about your experience..."
              style={{
                width: "100%",
                boxSizing: "border-box",
                background: "#f7f8fa",
                border: "1.5px solid #e8e8e8",
                borderRadius: "10px",
                padding: "12px 14px",
                fontSize: "0.85rem",
                fontFamily: "'Poppins', sans-serif",
                color: "#1a1a1a",
                resize: "vertical",
                outline: "none",
              }}
            />
          </div>

          <button
            onClick={() => this.submitFeedback()}
            disabled={!selectedRating || !feedbackText.trim()}
            style={{
              width: "100%",
              padding: "13px",
              border: "none",
              borderRadius: "10px",
              fontFamily: "'Poppins', sans-serif",
              fontSize: "0.9rem",
              fontWeight: 600,
              letterSpacing: "0.3px",
              cursor: (selectedRating && feedbackText.trim()) ? "pointer" : "default",
              background: (selectedRating && feedbackText.trim()) ? "#4a4a4a" : "#c5c8cc",
              color: "#fff",
              boxShadow: (selectedRating && feedbackText.trim()) ? "0 4px 14px rgba(0,0,0,0.18)" : "none",
              transition: "background 0.25s, box-shadow 0.25s",
            }}
          >
            Submit Feedback
          </button>
        </div>,
        document.body
      )
      : null;

    const liveQuizCard = liveQuiz
      ? (
        <div style={{
          position: "relative",
          backgroundColor: "#ffffff",
          border: "1px solid #e8e8e8",
          borderRadius: "10px",
          padding: "16px",
          width: "360px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
          fontFamily: "'Poppins', sans-serif",
        }}>
          <p style={{
            fontSize: "9px",
            fontWeight: 700,
            letterSpacing: "0.5px",
            color: "#16a34a",
            margin: "0 0 4px",
            textTransform: "uppercase",
          }}>
            Live Quiz Started
          </p>
          <h3 style={{
            fontSize: "0.9rem",
            fontWeight: 700,
            color: "#1a1a1a",
            margin: "0 24px 8px 0",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
            {liveQuiz.quizName || "Live Quiz"}
          </h3>
          <a
            href={liveQuiz.joinUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={liveQuiz.joinUrl}
            style={{
              display: "block",
              fontSize: "0.72rem",
              color: "#2563eb",
              marginBottom: "8px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {liveQuiz.joinUrl}
          </a>
          <a
            href={liveQuiz.joinUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: "0.78rem",
              fontWeight: 600,
              color: "#2563eb",
              textDecoration: "underline",
            }}
          >
            Join Quiz
          </a>
          <button
            onClick={() => this.setState({ liveQuiz: null })}
            aria-label="Dismiss"
            style={{
              position: "absolute",
              top: "8px",
              right: "10px",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "20px",
              color: "#999",
              lineHeight: 1,
              padding: 0,
            }}
          >
            &times;
          </button>
        </div>
      )
      : null;

    const recommendationPortal = recommendations.length > 0 || liveQuiz
      ? ReactDOM.createPortal(
        <div style={{
          position: "fixed",
          top: 0,
          right: 0,
          zIndex: 2147483647,
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "12px",
          maxHeight: "100vh",
          overflowY: "auto",
        }}>
          {liveQuizCard}
          {recommendations.map((product) => (
            <div
              key={product.id}
              style={{
                position: "relative",
                backgroundColor: "#ffffff",
                border: "1px solid #e8e8e8",
                borderRadius: "10px",
                padding: "16px",
                width: "360px",
                boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
                fontFamily: "'Poppins', sans-serif",
                display: "flex",
                gap: "14px",
                alignItems: "stretch",
                cursor: "default",
              }}
            >
              <img
                src={product.productImage || DEFAULT_PRODUCT_IMAGE}
                alt={product.productName || "Recommended course"}
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                }}
                style={{
                  width: "104px",
                  height: "auto",
                  alignSelf: "stretch",
                  minHeight: "104px",
                  borderRadius: "8px",
                  objectFit: "cover",
                  flexShrink: 0,
                  background: "#f3f4f6",
                }}
              />
              <div style={{ minWidth: 0, flex: 1, paddingRight: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                  <p style={{
                    fontSize: "9px",
                    fontWeight: 700,
                    letterSpacing: "0.5px",
                    color: "#f5a623",
                    margin: 0,
                    textTransform: "uppercase",
                  }}>
                    Recommended
                  </p>
                  {product.recommendedProductType && (
                    <span style={{
                      fontSize: "8px",
                      fontWeight: 700,
                      color: "#2563eb",
                      background: "#eaf1ff",
                      borderRadius: "4px",
                      padding: "2px 6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                    }}>
                      {product.recommendedProductType}
                    </span>
                  )}
                </div>
                <h3 style={{
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color: "#1a1a1a",
                  margin: "0 0 4px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {product.productName}
                </h3>
                {product.productDescription && (
                  <p
                    title={stripHtmlTags(product.productDescription)}
                    style={{
                      fontSize: "0.72rem",
                      color: "#666",
                      margin: 0,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {stripHtmlTags(product.productDescription)}
                  </p>
                )}
                <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      this.handleRecommendationClick(product, "view");
                    }}
                    aria-label="View"
                    title="View"
                    style={{
                      padding: "7px 14px",
                      border: "1.5px solid #2563eb",
                      borderRadius: "6px",
                      background: "#fff",
                      color: "#2563eb",
                      cursor: "pointer",
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      letterSpacing: "0.3px",
                    }}
                  >
                    View
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      this.handleRecommendationClick(product, "pay");
                    }}
                    aria-label="Pay Now"
                    title="Pay Now"
                    style={{
                      padding: "7px 14px",
                      border: "none",
                      borderRadius: "6px",
                      background: "#2563eb",
                      color: "#fff",
                      cursor: "pointer",
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      letterSpacing: "0.3px",
                    }}
                  >
                    Pay Now
                  </button>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  this.dismissRecommendation(product.id);
                }}
                aria-label="Dismiss"
                style={{
                  position: "absolute",
                  top: "6px",
                  right: "8px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "18px",
                  color: "#999",
                  lineHeight: 1,
                  padding: "2px",
                }}
              >
                &times;
              </button>
            </div>
          ))}
        </div>,
        document.body
      )
      : null;

    return (
      <div className="App" style={{ textAlign: "center" }}>
        {showAppLaunchPrompt && (
          <div style={{
            marginTop: "50px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "15px",
            padding: "0 20px",
          }}>
            <p style={{ color: "#111827", fontSize: "17px", fontWeight: 600 }}>
              Join your class
            </p>
            <button
              onClick={() => this.attemptAppJoin()}
              style={{
                padding: "12px 24px",
                backgroundColor: "#2563eb",
                color: "#ffffff",
                border: "none",
                borderRadius: "6px",
                fontSize: "16px",
                fontWeight: 600,
                cursor: "pointer",
                width: "100%",
                maxWidth: "320px",
              }}
            >
              Join with Zoom App
            </button>
            <button
              onClick={() => {
                this.setState({ showAppLaunchPrompt: false });
                this.handleJoinWeb();
              }}
              style={{
                padding: "10px 20px",
                backgroundColor: "transparent",
                color: "#2563eb",
                border: "1px solid #2563eb",
                borderRadius: "6px",
                fontSize: "15px",
                cursor: "pointer",
                width: "100%",
                maxWidth: "320px",
              }}
            >
              Join in Browser
            </button>
          </div>
        )}
        {(isLoading || checkingDesktop) && !showAppLaunchPrompt && (
          <div style={{
            marginTop: "50px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "15px",
          }}>
            <div style={{
              width: "50px",
              height: "50px",
              border: "4px solid #f3f4f6",
              borderTop: "4px solid #2563eb",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}></div>
            <p style={{ color: "#6b7280", fontSize: "16px" }}>
              Loading...
            </p>
            {showWebFallback && (
              <button
                onClick={() => {
                  if (this.webFallbackHandler) this.webFallbackHandler();
                }}
                style={{
                  marginTop: "10px",
                  padding: "10px 20px",
                  backgroundColor: "#2563eb",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "15px",
                  cursor: "pointer",
                }}
              >
                Join in Browser
              </button>
            )}
          </div>
        )}
        {feedbackPortal}
        {recommendationPortal}
        {sessionTimedOut && ReactDOM.createPortal(
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2147483647,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontFamily: "'Poppins', sans-serif",
            fontSize: "18px",
            fontWeight: 600,
          }}>
            Session timed out. Closing…
          </div>,
          document.body
        )}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
}

export default App;